import React, { useEffect, useMemo, useState } from 'react';
import {
    Box, Flex, Text, Badge, Icon, HStack, VStack, Input, Button, useToast,
    InputGroup, InputLeftAddon, InputRightAddon, FormControl, FormLabel, Tooltip, Image, SimpleGrid, Divider
} from '@chakra-ui/react';
import { FaSave, FaUndo, FaPercentage, FaTag, FaCalendarAlt, FaUser, FaQuestionCircle, FaIdBadge } from 'react-icons/fa';
import { salvarComissaoVenda } from '../../services/api';
import { checkPermission } from '../../services/permission';
import StatusComissao from './StatusComissao';
import { formatarCentavos, formatarData } from './formato';

const CORES_CANAL = {
    site: { bg: '#FFF4EB', border: '#F68633', label: 'Vendas do Site' },
    ml: { bg: '#FFF9E6', border: '#E6B800', label: 'Mercado Livre' },
    manual: { bg: '#F0FFF4', border: '#38A169', label: 'Venda Manual' },
};

const LOGOS_CANAL = {
    site: '/taga_logo.jpg',
    ml: '/ml_logo_2.webp',
    manual: '/taga_logo.jpg',
};

const REGRAS_CUPOM = {
    '': 3.0,
    'EMERSON5': 2.5,
    'EMERSON10': 2.0,
    'EMERSON15': 1.5,
    'EMERSON20': 1.0,
};

const PCT_PADRAO_SEM_CUPOM = 3.0;
const PCT_PADRAO_PERGUNTA_ML = 1.5;

const STATUS_VENDA_PROPS = {
    confirmada: { label: 'Confirmada', colorScheme: 'gray' },
    enviar: { label: 'Para Enviar', colorScheme: 'yellow' },
    transito: { label: 'Em Trânsito', colorScheme: 'blue' },
    concluida: { label: 'Concluída', colorScheme: 'green' },
    cancelada: { label: 'Cancelada', colorScheme: 'red' },
};

const getStatusVendaProps = (status) => {
    const key = String(status || '').toLowerCase().trim();
    return STATUS_VENDA_PROPS[key] || { label: status || '—', colorScheme: 'gray' };
};

const canalUsaCupom = (canal) => canal === 'site' || canal === 'manual';

const getPercentualPorCupom = (cupom) => {
    const key = String(cupom || '').trim().toUpperCase();
    return key in REGRAS_CUPOM ? REGRAS_CUPOM[key] : PCT_PADRAO_SEM_CUPOM;
};

const calcPercentualPadrao = (registro) => {
    if (registro.comissao_manual && registro.percentual != null) {
        return Number(registro.percentual);
    }
    if (registro.percentual_padrao != null) {
        return Number(registro.percentual_padrao);
    }
    if (registro.atribuicao_pergunta) {
        return PCT_PADRAO_PERGUNTA_ML;
    }
    if (canalUsaCupom(registro.canal)) {
        return getPercentualPorCupom(registro.cupom || '');
    }
    return Number(registro.percentual) || PCT_PADRAO_SEM_CUPOM;
};

const calcValorPadrao = (registro) => {
    if (registro.comissao_manual && registro.valor_comissao != null) {
        return Number(registro.valor_comissao);
    }
    const base = Number(registro.valor_base) || 0;
    return (base * calcPercentualPadrao(registro)) / 100;
};

const formatarMoedaInput = (valor) => {
    const num = Number(valor);
    if (isNaN(num)) return '0,00';
    return num.toFixed(2).replace('.', ',');
};

const parseMoedaInput = (str) => {
    if (str === null || str === undefined) return 0;
    const s = String(str).replace(/\./g, '').replace(',', '.').trim();
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
};

const sanitizeNumericBR = (raw, { maxDecimals = 2, max } = {}) => {
    if (raw === '' || raw === null || raw === undefined) return '';
    let s = String(raw).replace(/[^\d,]/g, '');
    const firstComma = s.indexOf(',');
    if (firstComma >= 0) {
        s = s.slice(0, firstComma + 1) + s.slice(firstComma + 1).replace(/,/g, '');
        const [intPart, decPart] = s.split(',');
        s = intPart + ',' + decPart.slice(0, maxDecimals);
    }
    if (max !== undefined && s !== '' && s !== ',') {
        const num = parseFloat(s.replace(',', '.'));
        if (!isNaN(num) && num > max) {
            return formatarMoedaInput(max);
        }
    }
    return s;
};

const LinhaComissaoVenda = ({ registro, userId, onSalvo }) => {
    const toast = useToast();
    const cor = CORES_CANAL[registro.canal] || CORES_CANAL.ml;
    const isAdm = checkPermission();
    // Comissao paga fica congelada: so muda desfazendo o pagamento na aba Pagamentos.
    const paga = registro.pagamento?.situacao === 'paga';
    const podeEditar = isAdm && !paga;

    const [percentual, setPercentual] = useState(formatarMoedaInput(calcPercentualPadrao(registro)));
    const [valorComissao, setValorComissao] = useState(formatarMoedaInput(calcValorPadrao(registro)));
    const [observacao, setObservacao] = useState(registro.observacao || '');
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        setPercentual(formatarMoedaInput(calcPercentualPadrao(registro)));
        setValorComissao(formatarMoedaInput(calcValorPadrao(registro)));
        setObservacao(registro.observacao || '');
    }, [registro.origem_id, registro.valor_comissao, registro.percentual, registro.cupom, registro.observacao, registro.valor_base, registro.canal, registro.comissao_manual, registro.partner_referral_code]);

    const valorBase = Number(registro.valor_base) || 0;
    const usaCupom = canalUsaCupom(registro.canal);
    const pctPadraoAtual = calcPercentualPadrao(registro);
    const cupomVenda = registro.cupom || '';
    const partnerReferralCode = registro.partner_referral_code || '';

    const recalcularComPercentual = (raw) => {
        const sanitized = sanitizeNumericBR(raw, { maxDecimals: 2, max: 100 });
        setPercentual(sanitized);
        const pct = parseMoedaInput(sanitized);
        const novoValor = (valorBase * pct) / 100;
        setValorComissao(formatarMoedaInput(novoValor));
    };

    const recalcularComValor = (raw) => {
        const sanitized = sanitizeNumericBR(raw, { maxDecimals: 2 });
        setValorComissao(sanitized);
        if (valorBase > 0) {
            const v = parseMoedaInput(sanitized);
            const pct = (v / valorBase) * 100;
            setPercentual(formatarMoedaInput(pct));
        }
    };

    const handleBlurPercentual = () => {
        if (percentual === '' || percentual === ',') {
            setPercentual('0,00');
            return;
        }
        setPercentual(formatarMoedaInput(parseMoedaInput(percentual)));
    };

    const handleBlurValor = () => {
        if (valorComissao === '' || valorComissao === ',') {
            setValorComissao('0,00');
            return;
        }
        setValorComissao(formatarMoedaInput(parseMoedaInput(valorComissao)));
    };

    const editado = useMemo(() => {
        const round2 = (n) => Math.round(n * 100) / 100;
        const pAtual = round2(parseMoedaInput(percentual));
        const vAtual = round2(parseMoedaInput(valorComissao));
        const pOriginal = round2(pctPadraoAtual);
        const vOriginal = round2((valorBase * pctPadraoAtual) / 100);
        const oAtual = (observacao || '').trim();
        const oOriginal = (registro.observacao || '').trim();

        return pAtual !== pOriginal
            || vAtual !== vOriginal
            || oAtual !== oOriginal;
    }, [percentual, valorComissao, observacao, registro, pctPadraoAtual, valorBase]);

    const handleSalvar = async () => {
        if (!isAdm) {
            toast({ title: 'Apenas administradores podem editar comissões', status: 'warning', isClosable: true });
            return;
        }
        setSalvando(true);
        const payload = {
            venda_id: registro.origem_id,
            user_id: userId,
            percentual: parseMoedaInput(percentual),
            valor_comissao: parseMoedaInput(valorComissao),
            observacao: observacao || null,
        };

        const res = await salvarComissaoVenda(payload);
        setSalvando(false);

        if (res && res.success) {
            toast({ title: 'Comissão salva com sucesso', status: 'success', duration: 2500, isClosable: true });
            if (onSalvo) onSalvo({ ...registro, ...payload, comissao_manual: true });
        } else {
            toast({ title: 'Erro ao salvar comissão', status: 'error', isClosable: true });
        }
    };

    const handleResetar = () => {
        if (!podeEditar) return;
        const pctReset = pctPadraoAtual;
        setPercentual(formatarMoedaInput(pctReset));
        setValorComissao(formatarMoedaInput((valorBase * pctReset) / 100));
        setObservacao('');
    };

    return (
        <Box
            bg="white"
            borderWidth="1px"
            borderRadius="xl"
            borderColor="gray.200"
            boxShadow="sm"
            p={{ base: 4, md: 5 }}
            transition="all 0.2s"
            _hover={{ boxShadow: 'md' }}
        >
            <Flex
                direction={{ base: 'column', lg: 'row' }}
                gap={4}
                align={{ base: 'stretch', lg: 'flex-start' }}
            >
                <VStack
                    align="stretch"
                    spacing={2}
                    flex={{ base: '1', lg: '0 0 320px' }}
                    bg={cor.bg}
                    borderRadius="lg"
                    borderLeftWidth="5px"
                    borderLeftColor={cor.border}
                    p={3}
                >
                    <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
                        <Badge
                            colorScheme="orange"
                            fontSize="xs"
                            px={2}
                            py={1}
                            borderRadius="full"
                            bg={cor.border}
                            color="white"
                        >
                            {cor.label}
                        </Badge>
                        <Image
                            src={LOGOS_CANAL[registro.canal] || '/taga_logo.jpg'}
                            alt={cor.label}
                            h="22px"
                            objectFit="contain"
                            borderRadius="md"
                        />
                    </Flex>

                    {registro.status && (() => {
                        const s = getStatusVendaProps(registro.status);
                        return (
                            <Badge
                                colorScheme={s.colorScheme}
                                fontSize="11px"
                                px={2}
                                py={1}
                                borderRadius="full"
                                w="fit-content"
                                textTransform="none"
                            >
                                {s.label}
                            </Badge>
                        );
                    })()}

                    {registro.pagamento && (
                        <StatusComissao
                            situacao={registro.pagamento.situacao}
                            sufixo={paga && registro.pagamento.pago_em ? `em ${formatarData(registro.pagamento.pago_em)}` : undefined}
                        />
                    )}

                    <HStack fontSize="sm" color="gray.700">
                        <Icon as={FaCalendarAlt} color="gray.500" />
                        <Text fontWeight="semibold">{registro.data_formatada}</Text>
                    </HStack>

                    <HStack fontSize="sm" color="gray.700">
                        <Icon as={FaUser} color="gray.500" />
                        <Text noOfLines={1}>{registro.cliente || 'Cliente não informado'}</Text>
                    </HStack>

                    <Box pt={1}>
                        <Text fontSize="xs" color="gray.600">ID Venda</Text>
                        <Text fontSize="sm" fontWeight="bold">{registro.origem_id}</Text>
                    </Box>

                    {registro.id_origem && (
                        <Box>
                            <Text fontSize="xs" color="gray.600">ID Origem</Text>
                            <Text fontSize="sm" fontWeight="bold">{registro.id_origem}</Text>
                        </Box>
                    )}

                    <Divider borderColor={cor.border} opacity={0.4} my={1} />

                    <Box>
                        <Text fontSize="xs" color="gray.600">Valor base da venda</Text>
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">
                            R$ {formatarMoedaInput(valorBase)}
                        </Text>
                    </Box>

                    {registro.atribuicao_pergunta && (
                        <Tooltip
                            hasArrow
                            placement="top"
                            label={
                                <Box fontSize="xs" lineHeight="1.4" maxW="320px">
                                    <Text fontWeight="bold" mb={1}>Atribuída via pergunta respondida</Text>
                                    <Text>Respondida em {registro.atribuicao_pergunta.data_formatada}</Text>
                                    {registro.atribuicao_pergunta.pergunta_texto && (
                                        <Text mt={1} fontStyle="italic" noOfLines={3}>"{registro.atribuicao_pergunta.pergunta_texto}"</Text>
                                    )}
                                </Box>
                            }
                        >
                            <Badge
                                colorScheme="purple"
                                px={2}
                                py={1}
                                borderRadius="md"
                                display="inline-flex"
                                alignItems="center"
                                gap={1.5}
                                fontSize="11px"
                                textTransform="none"
                                cursor="help"
                                w="fit-content"
                            >
                                <Icon as={FaQuestionCircle} boxSize={3} />
                                Via pergunta · {registro.atribuicao_pergunta.data_formatada}
                            </Badge>
                        </Tooltip>
                    )}
                </VStack>

                <Box flex="1">
                    <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                        <FormControl>
                            <FormLabel fontSize="sm" mb={1} color="gray.700">
                                <HStack><Icon as={FaPercentage} /> <Text>Percentual (%)</Text></HStack>
                            </FormLabel>
                            <InputGroup size="lg">
                                <Input
                                    value={percentual}
                                    onChange={(e) => recalcularComPercentual(e.target.value)}
                                    onBlur={handleBlurPercentual}
                                    isReadOnly={!podeEditar}
                                    bg={podeEditar ? 'white' : 'gray.50'}
                                    cursor={podeEditar ? 'text' : 'default'}
                                    fontSize="md"
                                    inputMode="decimal"
                                />
                                <InputRightAddon children="%" />
                            </InputGroup>
                        </FormControl>

                        <FormControl>
                            <FormLabel fontSize="sm" mb={1} color="gray.700">
                                Valor da comissão (R$)
                            </FormLabel>
                            <InputGroup size="lg">
                                <InputLeftAddon children="R$" />
                                <Input
                                    value={valorComissao}
                                    onChange={(e) => recalcularComValor(e.target.value)}
                                    onBlur={handleBlurValor}
                                    isReadOnly={!podeEditar}
                                    bg={podeEditar ? 'white' : 'gray.50'}
                                    cursor={podeEditar ? 'text' : 'default'}
                                    fontSize="md"
                                    fontWeight="bold"
                                    color="green.700"
                                    inputMode="decimal"
                                />
                            </InputGroup>
                        </FormControl>

                        {usaCupom && (
                            <FormControl>
                                <FormLabel fontSize="sm" mb={1} color="gray.700">
                                    <HStack spacing={1}>
                                        <Icon as={FaIdBadge} />
                                        <Text>ID do Parceiro</Text>
                                    </HStack>
                                </FormLabel>
                                <Input
                                    size="lg"
                                    value={partnerReferralCode || '—'}
                                    isReadOnly
                                    bg="gray.50"
                                    cursor="default"
                                    _focus={{ boxShadow: 'none', borderColor: 'gray.200' }}
                                />
                            </FormControl>
                        )}

                        {usaCupom && (
                            <FormControl>
                                <FormLabel fontSize="sm" mb={1} color="gray.700">
                                    <HStack spacing={1}>
                                        <Icon as={FaTag} />
                                        <Text>Cupom utilizado</Text>
                                    </HStack>
                                </FormLabel>
                                <Input
                                    size="lg"
                                    value={cupomVenda || '—'}
                                    isReadOnly
                                    bg="gray.50"
                                    cursor="default"
                                    _focus={{ boxShadow: 'none', borderColor: 'gray.200' }}
                                />
                            </FormControl>
                        )}

                        <FormControl gridColumn={{ sm: '1 / -1' }}>
                            <FormLabel fontSize="sm" mb={1} color="gray.700">Observação</FormLabel>
                            <Input
                                size="lg"
                                value={observacao}
                                onChange={(e) => setObservacao(e.target.value)}
                                isReadOnly={!podeEditar}
                                bg={podeEditar ? 'white' : 'gray.50'}
                                cursor={podeEditar ? 'text' : 'default'}
                                placeholder={podeEditar ? 'Anotação opcional' : ''}
                            />
                        </FormControl>
                    </SimpleGrid>

                    <Flex
                        mt={4}
                        gap={3}
                        direction={{ base: 'column', sm: 'row' }}
                        justify="space-between"
                        align={{ base: 'stretch', sm: 'center' }}
                    >
                        <HStack spacing={2} fontSize="sm" color="gray.600" flexWrap="wrap">
                            {registro.comissao_manual ? (
                                <Badge colorScheme="green" px={2} py={1} borderRadius="md">Comissão Salva</Badge>
                            ) : (
                                <Badge colorScheme="gray" px={2} py={1} borderRadius="md">
                                    Padrão ({formatarMoedaInput(pctPadraoAtual)}%)
                                </Badge>
                            )}
                            {editado && !paga && <Badge colorScheme="orange" px={2} py={1} borderRadius="md">Alterado</Badge>}
                        </HStack>

                        {isAdm && paga && (
                            <Text fontSize="xs" color="gray.600" maxW={{ sm: '320px' }} textAlign={{ base: 'left', sm: 'right' }}>
                                Paga {registro.pagamento.valor_pago_centavos != null ? `(${formatarCentavos(registro.pagamento.valor_pago_centavos)}) ` : ''}
                                no pagamento #{registro.pagamento.fechamento_id}. Para alterar, desfaça o pagamento na aba Pagamentos.
                            </Text>
                        )}

                        {podeEditar && (
                            <HStack spacing={2}>
                                <Tooltip label={`Voltar para o padrão (${formatarMoedaInput(pctPadraoAtual)}%)`}>
                                    <Button
                                        size="md"
                                        variant="outline"
                                        leftIcon={<Icon as={FaUndo} />}
                                        onClick={handleResetar}
                                    >
                                        Padrão {formatarMoedaInput(pctPadraoAtual)}%
                                    </Button>
                                </Tooltip>
                                <Button
                                    size="md"
                                    colorScheme="green"
                                    leftIcon={<Icon as={FaSave} />}
                                    onClick={handleSalvar}
                                    isLoading={salvando}
                                    isDisabled={!editado && registro.comissao_manual}
                                    minW="120px"
                                >
                                    Salvar
                                </Button>
                            </HStack>
                        )}
                    </Flex>
                </Box>
            </Flex>
        </Box>
    );
};

export default LinhaComissaoVenda;
